import React, { FC, memo, Fragment } from 'react';

import styled from 'styled-components';
import dynamic from 'next/dynamic';

import { useBreakpoint } from '@contexts/breakpoints';
import PriceDetailsDrawer from '@components/ui/PriceDetailsDrawer/PriceDetailsDrawer';
import { Currencies } from '@constants/constants';
import { Row, FlexibleDiv } from '@components/formStyles';
import {
  ItemText,
  ItemBoldText,
  WarningMessage,
  ItemsCol,
  ItemContentRow,
} from '@components/ui/PriceDetailsDrawer/Item';
import CustomIcon from '@components/utils/CustomIcon';
import { HotelIcon } from '@assets/icons';
import { Guest } from '@components/ui/guest-box/guest-box-tab/GuestBox';

const ListItemCircle = dynamic(
  () =>
    import('@components/ui/PriceDetailsDrawer/Item').then(
      (mod: any) => mod['ListItemCircle']
    ) as Promise<React.FC<any>>
);
export interface PriceDetailsDrawerItem {
  tabNumber: number;
  id: number;
  price: number;
  title: string;
  day?: number;
  nightCount?: number;
  guests: Guest[];
  packageDetailId?: number;
}

const ItemContent = styled.div<{ marginBottom?: number }>`
  margin-bottom: ${({ marginBottom = 0 }) => `${marginBottom}px`};
`;

export interface PriceDetailsProps {
  items: PriceDetailsDrawerItem[];
  onNavigate: () => void;
  onRemove: (id: number, packageId: number) => void;
  currency?: string;
  warningMessage?: string;
  totalPrice: number;
  originalPrice?: number;
  originalCurrency?: string;
  isExpedia?: boolean;
  totalMilePrice: number;
  toggleConfirmModal: () => void;
  itemsInBasket?: number;
}

const PriceDetails: FC<PriceDetailsProps> = ({
  onNavigate,
  onRemove,
  items = [],
  currency = Currencies.TL,
  warningMessage,
  totalPrice,
  isExpedia,
  totalMilePrice = 0,
  toggleConfirmModal,
  itemsInBasket,
}) => {
  const { xl: isDesktop } = useBreakpoint();

  if (isDesktop === undefined) return null;

  const handleNavigateTravelPlanning = () => {
    if (itemsInBasket === 0) {
      onNavigate();
    } else {
      toggleConfirmModal();
    }
  };

  return (
    <PriceDetailsDrawer
      closable={isDesktop ? items.length > 2 : true}
      totalPrice={totalPrice}
      totalMilePrice={totalMilePrice}
      onNavigate={handleNavigateTravelPlanning}
      disabled={!!warningMessage}
      actionButtonTitle='Devam Et'
      isExpedia={isExpedia}
    >
      <Row>
        <CustomIcon
          fontSize={24}
          icon={HotelIcon}
          paletteColor='primaryBrand'
        />
        <FlexibleDiv right={10} />
        <ItemsCol>
          {items
            .sort((a, b) => (a.tabNumber > b.tabNumber ? 1 : -1))
            ?.map((item, index) => (
              <PriceDetailsDrawer.Item
                key={item.id}
                content={
                  <ItemContent marginBottom={index !== items.length ? 10 : 0}>
                    {item.guests?.map((room) => (
                      <Fragment key={room.id}>
                        <ItemContentRow alignItems='center'>
                          <ItemBoldText>{item.title}</ItemBoldText>
                          {isDesktop ? <ListItemCircle /> : null}
                          {item.day && item.nightCount ? (
                            <ItemText>{`${item.day} Gün ${item.nightCount} Gece`}</ItemText>
                          ) : null}
                        </ItemContentRow>

                        <ItemContentRow
                          alignItems='center'
                          style={{ flexWrap: 'wrap' }}
                          key={room.id}
                        >
                          <ItemBoldText>{`1 Adet ${room.adultNumber} Kişilik Oda`}</ItemBoldText>
                          {isDesktop ? <ListItemCircle /> : null}
                          <ItemText>{`${room.adultNumber} Yetişkin`}</ItemText>
                          &nbsp;
                          {room.childList?.length ? (
                            <ItemText>{`${room.childList.length} Çocuk`}</ItemText>
                          ) : null}
                          &nbsp;
                        </ItemContentRow>
                      </Fragment>
                    ))}
                  </ItemContent>
                }
                currency={currency}
                onRemove={() =>
                  item.packageDetailId &&
                  onRemove(item.packageDetailId, item.id)
                }
                price={item.price || 0}
                showPrice={false}
              />
            ))}
        </ItemsCol>
      </Row>
      {warningMessage ? (
        <WarningMessage>{warningMessage}</WarningMessage>
      ) : null}
    </PriceDetailsDrawer>
  );
};

export default memo(PriceDetails);
